import type {
  Amount,
  CheckoutInfo,
  CheckoutPrice,
  PaymentMethod,
  Product,
  SectionProductDetail
} from '@space-shell/common/services/sections'
import { PaymentTypes, SummaryType } from '@space-shell/common/services/sections'

export enum UserType {
  STUDENT = 'STUDENT',
  VISITOR = 'VISITOR'
}

export const EMPTY_COUPON = 'NO_COUPON'

export const DESCRIPTION_PLAN_MAX = 165

export const getProductCoupon = (product: Product | SectionProductDetail): number => {
  const PERCENTAGE = 100

  const isSubscriptionPlan = !!product?.plan?.currencyValue?.value
  const hasNoDiscount = !product?.couponInfo?.discount || !product?.couponInfo?.monthValue
  const isPurchased = !!product?.purchased
  const hasNoCheckoutInfo = !product?.checkoutInfo
  const hasNoPaymentMethods = !Array.isArray(product?.checkoutInfo?.paymentMethods)

  if (
    (isSubscriptionPlan && (hasNoDiscount || isPurchased)) ||
    (!isSubscriptionPlan && (hasNoCheckoutInfo || hasNoPaymentMethods || isPurchased))
  ) {
    return 0
  }

  return product?.couponInfo?.discount && product?.couponInfo?.discount > 0
    ? Math.round(product.couponInfo.discount * PERCENTAGE)
    : 0
}

export const getCouponStudent = (product: Product | SectionProductDetail): UserType | null => {
  return product?.couponInfo?.userType && product?.couponInfo?.userType === UserType.STUDENT
    ? UserType.STUDENT
    : null
}

function getCreditCardPayment(checkoutInfo: CheckoutInfo) {
  return checkoutInfo.paymentMethods.find((method) => method?.type === PaymentTypes.CREDIT_CARD)
}

export function getTaxesPrice(creditCardPayment: PaymentMethod | undefined) {
  const taxPriceItem = creditCardPayment?.summary?.items.find(
    (item) => item?.type === SummaryType.TAX_FEDERAL
  )

  if (!taxPriceItem) {
    return 0
  }

  return taxPriceItem.value > 0 ? taxPriceItem.value : 0
}

function getFullPriceAmount(creditCardPayment: PaymentMethod | undefined) {
  const fullPriceItem = creditCardPayment?.summary?.items.find(
    (item) => item?.type === 'FULL_PRICE'
  )

  const totalPriceItem =
    fullPriceItem && fullPriceItem?.value > 0
      ? fullPriceItem?.value + getTaxesPrice(creditCardPayment)
      : 0

  return fullPriceItem ? { value: totalPriceItem, currency: fullPriceItem.currency } : null
}

export const formatPriceValue = ({
  locale,
  amount
}: {
  locale: string
  amount: Amount
}): string | null => {
  const currency = amount?.currency
  if (!locale) {
    locale = navigator.language
  }

  if (currency) {
    return new Intl.NumberFormat(locale.replace('_', '-'), {
      style: 'currency',
      currency: currency
    }).format(amount?.value)
  }

  return null
}

export const getProductActivePrice = (
  isSubscription: boolean,
  checkoutPrice: CheckoutPrice | null
) => {
  if (isSubscription) {
    return checkoutPrice?.discountedPrice?.fullPrice || checkoutPrice?.fullPrice
  }

  return checkoutPrice?.price
}

export const getProductPrice = (product: Product | SectionProductDetail, locale: string) => {
  if (!product.checkoutInfo || !Array.isArray(product.checkoutInfo.paymentMethods)) {
    return null
  }

  const creditCardPayment = getCreditCardPayment(product.checkoutInfo)
  const fullAmount = getFullPriceAmount(creditCardPayment)
  const amount = creditCardPayment?.amount ?? null

  const formattedCreditCardPrice =
    amount?.value && amount?.currency ? formatPriceValue({ amount: amount, locale }) : null

  const formattedFullPrice =
    fullAmount?.value && fullAmount?.currency
      ? formatPriceValue({ amount: fullAmount, locale })
      : null

  return {
    price: formattedCreditCardPrice,
    fullPrice: formattedFullPrice
  }
}

export const isHubUrl = (url: string) => url.includes(`${process.env.HUB_URL}`)
